// src/pages/Inventory/Inventory.jsx

import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";

import UploadInventory from "./UploadInventory";
import InventoryFilter from "./InventoryFilter";
import InventoryTable from "./InventoryTable";
import DeleteInventoryDialog from "./DeleteInventoryDialog";

import InventoryServices from "../../services/InventoryServices";

const Inventory = () => {
  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);

  const [uploadLoading, setUploadLoading] = useState(false);

  const [page, setPage] = useState(1);

  const [pageSize] = useState(50);

  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");

  const [providerStatus, setProviderStatus] = useState("");

  const [selectedRows, setSelectedRows] = useState([]);

  const [selectedFile, setSelectedFile] = useState(null);

  const [deleteDialog, setDeleteDialog] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  //--------------------------------------------------
  // Snackbar
  //--------------------------------------------------

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  //--------------------------------------------------
  // Inventory List API
  //--------------------------------------------------

  const getInventoryList = async () => {
    setLoading(true);

    try {
      const payload = {
        page,
        page_size: pageSize,
        search,
        filters: providerStatus
          ? {
              provider_sim_status: providerStatus,
            }
          : {},
      };

      const response = await InventoryServices.inventoryList(payload);

      setRows(response.data.results || []);

      setTotal(response.data.total || 0);
    } catch (error) {
      showSnackbar(
        error.response?.data?.error || "Unable to load inventory",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  //--------------------------------------------------
  // Upload
  //--------------------------------------------------

  const handleUpload = async () => {
    if (!selectedFile) {
      showSnackbar("Please select an Excel file.", "warning");
      return;
    }

    try {
      setUploadLoading(true);

      const formData = new FormData();

      formData.append("excel_file", selectedFile);

      await InventoryServices.uploadInventory(formData);

      showSnackbar("Inventory uploaded successfully");

      setSelectedFile(null);

      getInventoryList();
    } catch (error) {
      showSnackbar(
        error.response?.data?.error || "Upload failed",
        "error"
      );
    } finally {
      setUploadLoading(false);
    }
  };

  //--------------------------------------------------
  // Delete
  //--------------------------------------------------

  const handleDelete = async () => {
    try {
       const response = await InventoryServices.deleteInventory({
      ids: selectedRows,
    });

       // Use API response message
    const message =
      response?.data?.message || "Inventory deleted successfully";

    // If records were skipped, show warning; otherwise success
    const severity =
      response?.data?.skipped_tagged?.length > 0
        ? "warning"
        : "success";

    showSnackbar(message, severity);

      setDeleteDialog(false);

      setSelectedRows([]);

      getInventoryList();
    } catch (error) {
      showSnackbar(
        error.response?.data?.error || "Delete failed",
        "error"
      );
    }
  };

  //--------------------------------------------------
  // Search
  //--------------------------------------------------

  const handleSearch = () => {
    setPage(1);

    getInventoryList();
  };

  //--------------------------------------------------
  // Reset
  //--------------------------------------------------

  const handleReset = () => {
    setSearch("");

    setProviderStatus("");

    setPage(1);
  };

  //--------------------------------------------------
  // Pagination
  //--------------------------------------------------

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  //--------------------------------------------------
  // Load Data
  //--------------------------------------------------

  useEffect(() => {
    getInventoryList();
  }, [page]);

  //--------------------------------------------------

  return (
    <Box p={2}>
      {/* Upload */}

      <Card sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h6"
          fontWeight={600}
          mb={3}
        >
          Upload Inventory File
        </Typography>

        <UploadInventory
          file={selectedFile}
          setFile={setSelectedFile}
          onUpload={handleUpload}
          loading={uploadLoading}
        />
      </Card>

      {/* Search */}

      <Card sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h6"
          fontWeight={600}
          mb={3}
        >
          Search / Filter
        </Typography>

        <InventoryFilter
          search={search}
          setSearch={setSearch}
          providerStatus={providerStatus}
          setProviderStatus={setProviderStatus}
          onSearch={handleSearch}
          onReset={handleReset}
          onDelete={() => setDeleteDialog(true)}
          selectedCount={selectedRows.length}
        />
      </Card>

      {/* Inventory */}

      <Card sx={{ p: 3 }}>
        <Typography
          variant="h6"
          fontWeight={600}
          mb={3}
        >
          Inventory
        </Typography>

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            py={8}
          >
            <CircularProgress />
          </Box>
        ) : (
          <InventoryTable
            rows={rows}
            page={page}
            total={total}
            pageSize={pageSize}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            onPageChange={handlePageChange}
          />
        )}
      </Card>

      {/* Delete Dialog */}

      <DeleteInventoryDialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onDelete={handleDelete}
        count={selectedRows.length}
      />

      {/* Snackbar */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        anchorOrigin={{
        vertical: "top",
        horizontal: "right",
    }}
        onClose={() =>
          setSnackbar({
            ...snackbar,
            open: false,
          })
        }
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Inventory;